import { useState, useEffect } from 'react';
import { Mail, Send, RefreshCw, Plus, X, CreditCard as Edit2, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import InfluencerAvatar from '../components/InfluencerAvatar';
import EmailThreadList from '../components/talent-manager/EmailThreadList';
import EmailThreadView from '../components/talent-manager/EmailThreadView';
import EmailReplyComposer from '../components/talent-manager/EmailReplyComposer';

function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

function cleanEmailContent(text: string): string {
  if (!text) return '';

  let cleaned = text;

  const quotePatterns = [
    /Le .+? à .+?, .+? <.+?> a écrit[\s\S]*/gi,
    /On .+?, .+? <.+?> wrote[\s\S]*/gi,
    /\d{1,2} \w+\.? \d{4} à \d{1,2}:\d{2}, .+? <.+?> a écrit[\s\S]*/gi,
    /Le ven\. \d{1,2} \w+\.? \d{4} à \d{1,2}:\d{2}, .+? <.+?> a écrit[\s\S]*/gi,
    /Le \d{1,2}\/\d{1,2}\/\d{4} à \d{1,2}:\d{2}, .+? a écrit[\s\S]*/gi,
    /_{10,}[\s\S]*/g,
    /On \w+, \w+ \d+, \d{4} at \d+:\d+ [AP]M .+? wrote[\s\S]*/gi,
    /De :.*\nEnvoyé :.*\nÀ :.*\nObjet :[\s\S]*/gi,
    /From:.*\nSent:.*\nTo:.*\nSubject:[\s\S]*/gi,
  ];

  for (const pattern of quotePatterns) {
    cleaned = cleaned.replace(pattern, '');
  }

  const commonFooters = [
    /Envoyé de mon iPhone/gi,
    /Sent from my iPhone/gi,
    /Get Outlook for iOS/gi,
    /Envoyé depuis Yahoo Mail.*/gi,
    /This email was sent from.*/gi,
    /Envoyé depuis mon appareil Galaxy/gi,
    /Sent from my Galaxy/gi,
    /Télécharger Outlook pour iOS/gi,
    /Download Outlook for iOS/gi,
  ];

  for (const pattern of commonFooters) {
    cleaned = cleaned.replace(pattern, '');
  }

  cleaned = cleaned
    .split('\n')
    .filter(line => {
      const trimmed = line.trim();
      return !trimmed.startsWith('>') &&
             !trimmed.startsWith('|') &&
             trimmed !== '--';
    })
    .join('\n');

  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  cleaned = cleaned.trim();

  return cleaned;
}

function detectEmailCategory(subject: string, snippet: string): string {
  const text = `${subject} ${snippet}`.toLowerCase();

  if (text.match(/\b(collaboration|collab|partenariat|partnership|campagne|campaign|contrat|contract|brief|posting|publication)\b/i)) {
    return 'collab';
  }

  if (text.match(/\b(gift|cadeau|gifted|pr package|envoi produit|produit offert|échantillon|sample)\b/i)) {
    return 'gift';
  }

  if (text.match(/\b(event|événement|évènement|invitation|invite|soirée|lancement|launch|rendez-vous|rdv|meeting|réunion)\b/i)) {
    return 'event';
  }

  if (text.match(/\b(facture|invoice|paiement|payment|contrat|contract|administratif|admin|compte|account|impôt|tax|devis|quote)\b/i)) {
    return 'admin';
  }

  if (text.match(/\b(spam|publicité|promo|newsletter|unsubscribe|désabonner)\b/i)) {
    return 'spam';
  }

  return 'other';
}

interface Creator {
  id: string;
  name: string;
  avatar?: string;
  email?: string;
  unread_count: number;
  gmail_connected: boolean;
  connected_email?: string;
  oauth_connection_id?: string;
}

interface EmailMessage {
  id: string;
  from_addr: string;
  to_addr?: string;
  subject: string;
  snippet: string;
  body_text?: string;
  body_html?: string;
  ai_summary?: string;
  classification?: string;
  received_at: string;
  is_read: boolean;
  direction: 'in' | 'out';
  gmail_thread_id: string;
  gmail_message_id: string;
  creator_id?: string;
}

function getReplyRecipient(email: EmailMessage, creatorEmail?: string): string {
  if (email.direction === 'in') {
    return email.from_addr;
  }

  if (email.direction === 'out' && email.to_addr) {
    return email.to_addr;
  }

  return email.from_addr;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  payload?: any;
  created_at: string;
}

export default function TalentManager() {
  const [user, setUser] = useState<any>(null);
  const [creators, setCreators] = useState<Creator[]>([]);
  const [selectedCreator, setSelectedCreator] = useState<Creator | null>(null);
  const [emails, setEmails] = useState<EmailMessage[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null);
  const [threadMessages, setThreadMessages] = useState<EmailMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [showThreadModal, setShowThreadModal] = useState(false);
  const [draftContent, setDraftContent] = useState('');
  const [draftSubject, setDraftSubject] = useState('');
  const [ccCreator, setCcCreator] = useState(true);
  const [sendAsCreator, setSendAsCreator] = useState(false);
  const [ccManager, setCcManager] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [streamingDraft, setStreamingDraft] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);
  const [showReplyComposer, setShowReplyComposer] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'thread'>('list');
  const [collaborations, setCollaborations] = useState<any[]>([]);


  useEffect(() => {
    checkAuth();
    handleOAuthCallback();
  }, []);

  useEffect(() => {
    if (user) {
      loadCreators();
    }
  }, [user]);

  useEffect(() => {
    if (selectedCreator && user?.agency_id) {
      loadEmails(selectedCreator.id);
      loadChatMessages(selectedCreator.id);
      loadCollaborations(selectedCreator.id);
    }
  }, [selectedCreator, user]);

  async function checkAuth() {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (authUser) {
      const { data: userData } = await supabase
        .from('users')
        .select('id, email, agency_id')
        .eq('id', authUser.id)
        .single();

      setUser(userData);
      checkGmailConnection(authUser.id);
    }
  }

  async function checkGmailConnection(userId: string) {
    // Plus besoin de vérifier la connexion du TM
    // Seuls les créateurs connectent leur Gmail
  }

  async function handleOAuthCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (!code || !state) return;

    setLoading(true);
    try {
      const [userId, creatorId] = state.split('|');
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        console.error('No session for OAuth callback');
        window.history.replaceState({}, '', '/app/talent-manager');
        setLoading(false);
        return;
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/gmail-connect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'exchange_code',
          code,
          creator_id: creatorId,
          redirect_uri: `${window.location.origin}/api/oauth/google/callback`,
        }),
      });

      const result = await response.json();
      console.log('OAuth exchange result:', result);

      if (result.success) {
        await loadCreators();
        setNotification('Gmail connecté avec succès');
        setTimeout(() => setNotification(null), 3000);
      } else {
        console.error('OAuth exchange failed:', result);
      }

      window.history.replaceState({}, '', '/app/talent-manager');
    } catch (error) {
      console.error('OAuth callback error:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadCreators() {
    if (!user) return;

    const { data: userData } = await supabase
      .from('users')
      .select('id, agency_id, email')
      .eq('id', user.id)
      .single();

    if (!userData?.agency_id) {
      setCreators([]);
      return;
    }


    const { data } = await supabase
      .from('organization_influencers')
      .select('*, influencers(id, display_name, profile_image_url, email)')
      .eq('organization_id', userData.agency_id);

    if (data) {
      const creatorsWithStatus = await Promise.all(
        data.map(async (orgInf: any) => {
          const creator = orgInf.influencers;
          if (!creator) return null;

          const { count } = await supabase
            .from('email_messages')
            .select('*', { count: 'exact', head: true })
            .eq('creator_id', creator.id)
            .eq('is_read', false)
            .eq('direction', 'in');

          const { data: conn } = await supabase
            .from('oauth_connections')
            .select('id, email')
            .eq('creator_id', creator.id)
            .eq('manager_id', user.id)
            .eq('is_active', true)
            .maybeSingle();

          return {
            id: creator.id,
            name: creator.display_name || 'Créateur',
            avatar: creator.profile_image_url,
            email: creator.email,
            unread_count: count || 0,
            gmail_connected: !!conn,
            connected_email: conn?.email,
            oauth_connection_id: conn?.id,
          };
        })
      );
      const filteredCreators = creatorsWithStatus.filter(c => c !== null) as Creator[];
      filteredCreators.sort((a, b) => {
        if (a.gmail_connected && !b.gmail_connected) return -1;
        if (!a.gmail_connected && b.gmail_connected) return 1;
        return 0;
      });

      // Plus d'entrée "Mes emails" pour le TM
      setCreators(filteredCreators);
    }
  }


  function normalizeSubject(subject: string): string {
    return subject
      .replace(/^(Re|Fwd|Fw|TR|RE|FWD):\s*/gi, '')
      .trim()
      .toLowerCase();
  }

  function extractEmailAddresses(email: any): Set<string> {
    const addresses = new Set<string>();

    const extractAddr = (addr: string) => {
      const match = addr.match(/<(.+?)>/);
      return match ? match[1].toLowerCase() : addr.toLowerCase();
    };

    if (email.from_addr) addresses.add(extractAddr(email.from_addr));
    if (email.to_addr) addresses.add(extractAddr(email.to_addr));
    if (email.cc && Array.isArray(email.cc)) {
      email.cc.forEach(addr => addresses.add(extractAddr(addr)));
    }

    return addresses;
  }

  async function loadEmails(creatorId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    console.log('Loading emails for creator:', creatorId, 'manager:', user?.id);

    const { data: allEmails, error: emailsError } = await supabase
      .from('email_messages')
      .select('*')
      .eq('creator_id', creatorId)
      .order('received_at', { ascending: false });

    console.log('Loaded emails:', allEmails?.length, 'error:', emailsError);

    if (!allEmails || allEmails.length === 0) {
      setEmails([]);
      return;
    }

    const conversationGroups = new Map<string, any[]>();

    allEmails.forEach(email => {
      const conversationKey = email.conversation_key || email.gmail_thread_id;

      if (!conversationGroups.has(conversationKey)) {
        conversationGroups.set(conversationKey, []);
      }
      conversationGroups.get(conversationKey)!.push(email);
    });

    const creatorEmail = (creators.find(c => c.id === creatorId)?.email ||
      creators.find(c => c.id === creatorId)?.connected_email || '').toLowerCase();

    const latestEmailPerConversation = Array.from(conversationGroups.values())
      .map(group => {
        const sortedGroup = group.sort((a, b) =>
          new Date(b.received_at).getTime() - new Date(a.received_at).getTime()
        );

        const receivedOnly = sortedGroup.filter(email => {
          const fromAddr = email.from_addr?.toLowerCase() || '';
          return !fromAddr.includes(creatorEmail);
        });

        if (receivedOnly.length === 0) return null;

        const latestEmail = receivedOnly[0];
        latestEmail.unified_thread_emails = sortedGroup;
        latestEmail.unified_thread_id = sortedGroup.map(e => e.gmail_thread_id).join('|');

        return latestEmail;
      })
      .filter(email => email !== null)
      .sort((a, b) => new Date(b.received_at).getTime() - new Date(a.received_at).getTime())
      .slice(0, 50);

    setEmails(latestEmailPerConversation);
  }

  async function syncCreatorEmails(oauthConnectionId: string) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

    const response = await fetch(`${SUPABASE_URL}/functions/v1/gmail-sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        creator_id: oauthConnectionId,
        force_resync: false,
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      console.error('Sync failed:', error);
      throw new Error('Failed to sync emails');
    }

    const result = await response.json();
    console.log('Sync completed:', result);
  }

  async function loadCollaborations(influencerId: string) {
    if (!user?.agency_id) {
      console.log('[LOAD COLLABS] No agency_id for user');
      return;
    }

    console.log('[LOAD COLLABS] Loading collabs for influencer_id:', influencerId);
    console.log('[LOAD COLLABS] User agency_id:', user.agency_id);

    const { data, error } = await supabase
      .from('collabs')
      .select(`
        id,
        name,
        brand,
        date,
        contract_signed,
        post_progress,
        agency_id,
        link_token
      `)
      .eq('agency_id', user.agency_id)
      .contains('influencer_ids', [influencerId])
      .order('date', { ascending: false });

    console.log('[LOAD COLLABS] Query filters - agency_id:', user.agency_id, 'influencer_id:', influencerId);
    console.log('[LOAD COLLABS] Found collaborations:', data?.length, 'Error:', error);
    console.log('[LOAD COLLABS] Data:', data);
    setCollaborations(data || []);
  }

  async function loadThreadMessages(threadId: string) {
    const { data: { user } } = await supabase.auth.getUser();

    const selectedEmail = emails.find(e =>
      e.gmail_thread_id === threadId || e.unified_thread_id === threadId
    );

    console.log('[LOAD THREAD] threadId:', threadId);
    console.log('[LOAD THREAD] selectedEmail:', selectedEmail);
    console.log('[LOAD THREAD] unified_thread_emails:', selectedEmail?.unified_thread_emails);

    if (selectedEmail?.unified_thread_emails) {
      const sortedMessages = selectedEmail.unified_thread_emails.sort((a, b) =>
        new Date(a.received_at).getTime() - new Date(b.received_at).getTime()
      );
      console.log('[LOAD THREAD] sortedMessages:', sortedMessages);
      setThreadMessages(sortedMessages);
      setViewMode('thread');
      return;
    }

    const { data } = await supabase
      .from('email_messages')
      .select('*')
      .eq('gmail_thread_id', threadId)
      .eq('creator_id', selectedCreator?.id)
      .order('received_at', { ascending: true });
    console.log('[LOAD THREAD] data from DB:', data);

    if (data) {
      setThreadMessages(data);
      setViewMode('thread');
    }
  }

  function prepareThreadsForList() {
    return emails.map(email => ({
      id: email.id,
      gmail_thread_id: email.unified_thread_id || email.gmail_thread_id,
      subject: email.subject,
      from_addr: email.from_addr,
      snippet: email.snippet,
      received_at: email.received_at,
      is_read: email.is_read,
      is_first_contact: (email as any).is_first_contact,
      classification: email.classification,
      message_count: email.unified_thread_emails?.length || 1,
    }));
  }

  async function handleThreadSelect(thread: any) {
    const email = emails.find(e =>
      e.gmail_thread_id === thread.gmail_thread_id ||
      e.unified_thread_id === thread.gmail_thread_id
    );

    setSelectedEmail(email || null);
    await loadThreadMessages(thread.gmail_thread_id);
  }

  function handleBackToList() {
    setViewMode('list');
    setThreadMessages([]);
    setSelectedEmail(null);
  }

  async function handleSendReply(params: {
    subject: string;
    body: string;
    ccCreator: boolean;
    ccManager: boolean;
    sendAsCreator: boolean;
    attachments: File[];
  }) {
    if (!selectedEmail || !selectedCreator) {
      throw new Error('Aucun email ou créateur sélectionné');
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Session non trouvée');
    }

    let uploadedAttachments: Array<{filename: string, url: string, size: number}> = [];

    if (params.attachments.length > 0) {
      for (const file of params.attachments) {
        const filePath = `email-attachments/${user?.id}/${Date.now()}-${file.name}`;
        const { data, error: uploadError } = await supabase.storage
          .from('resources')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          throw new Error(`Erreur upload ${file.name}: ${uploadError.message}`);
        }

        const { data: { publicUrl } } = supabase.storage
          .from('resources')
          .getPublicUrl(filePath);

        uploadedAttachments.push({
          filename: file.name,
          url: publicUrl,
          size: file.size
        });
      }
    }

    const recipientEmail = getReplyRecipient(selectedEmail, selectedCreator?.email);

    const actualCreatorId = selectedCreator?.id;

    if (!actualCreatorId) {
      throw new Error('Impossible de déterminer le créateur pour cet email');
    }

    console.log('[SEND DEBUG]', {
      selectedCreatorId: selectedCreator?.id,
      actualCreatorId,
      recipientEmail,
      replyingToMessageId: selectedEmail.id,
      threadMessagesCount: threadMessages.length,
      selectedEmailFrom: selectedEmail.from_addr,
      selectedEmailDate: selectedEmail.received_at,
      managerName: user?.email?.split('@')[0],
      managerEmail: user?.email
    });

    const response = await fetch(`${supabaseUrl}/functions/v1/gmail-send`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: recipientEmail,
        subject: params.subject,
        body: params.body,
        creator_id: actualCreatorId,
        reply_to_message_id: selectedEmail.id,
        attachments: uploadedAttachments,
        manager_name: user?.email?.split('@')[0] || 'Manager',
        manager_email: user?.email,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      console.error('[SEND ERROR]', result);
      const errorMsg = typeof result.error === 'string'
        ? result.error
        : JSON.stringify(result.error || result);
      throw new Error(`Failed to send email: ${errorMsg}`);
    }

    setNotification('Mail envoyé avec succès depuis le compte du créateur');
    setTimeout(() => setNotification(null), 3000);

    await loadThreadMessages(selectedEmail.gmail_thread_id);
    await loadEmails(selectedCreator.id);

    return result;
  }

  async function loadChatMessages(creatorId: string) {
    const { data } = await supabase
      .from('ai_chat_messages')
      .select('*')
      .eq('creator_id', creatorId)
      .order('created_at', { ascending: true })
      .limit(100);

    if (data) {
      const uniqueMessages = new Map();

      data.forEach(msg => {
        const threadId = msg.payload?.thread_id;

        if (threadId && msg.role !== 'system') {
          const existingMsg = uniqueMessages.get(threadId);
          if (!existingMsg || new Date(msg.created_at) > new Date(existingMsg.created_at)) {
            uniqueMessages.set(threadId, msg);
          }
        } else {
          uniqueMessages.set(msg.id, msg);
        }
      });

      const messages = Array.from(uniqueMessages.values());

      // Enrichir les messages avec l'info has_reply depuis email_messages
      const enrichedMessages = await Promise.all(
        messages.map(async (msg) => {
          if (msg.payload?.email_id) {
            const { data: emailData } = await supabase
              .from('email_messages')
              .select('has_reply')
              .eq('id', msg.payload.email_id)
              .maybeSingle();

            if (emailData) {
              return { ...msg, payload: { ...msg.payload, has_reply: emailData.has_reply } };
            }
          }
          return msg;
        })
      );

      setChatMessages(enrichedMessages);

      // Réorganiser les emails pour prioriser ceux dans le chat
      const chatThreadIds = new Set(
        enrichedMessages
          .map(msg => msg.payload?.thread_id)
          .filter(Boolean)
      );

      setEmails(prevEmails => {
        const emailsInChat = prevEmails.filter(e => chatThreadIds.has(e.gmail_thread_id));
        const emailsNotInChat = prevEmails.filter(e => !chatThreadIds.has(e.gmail_thread_id));
        return [...emailsInChat, ...emailsNotInChat];
      });
    }
  }

  async function connectGmail(creatorId?: string) {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      console.error('No session found');
      return;
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/gmail-connect`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'get_auth_url',
        creator_id: creatorId,
        redirect_uri: `${window.location.origin}/api/oauth/google/callback`,
      }),
    });

    const result = await response.json();
    if (result.auth_url) {
      window.location.href = result.auth_url;
    } else {
      console.error('No auth URL received:', result);
    }
  }


  async function syncEmails(forceResync = false) {
    console.log('[SYNC EMAILS] Fonction appelée, selectedCreator:', selectedCreator);

    if (!selectedCreator || !selectedCreator.oauth_connection_id) {
      console.log('[SYNC EMAILS] Pas de connexion OAuth');
      setError('Connexion OAuth non trouvée');
      return;
    }

    console.log('[SYNC EMAILS] Démarrage sync...');
    setSyncing(true);
    setError(null);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Session expirée, veuillez vous reconnecter');
        return;
      }

      if (forceResync) {
        const { error: deleteError } = await supabase
          .from('email_messages')
          .delete()
          .eq('creator_id', selectedCreator.id);

        if (deleteError) {
          console.error('Error deleting messages:', deleteError);
          setError('Erreur lors de la suppression des anciens messages');
          return;
        }
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/gmail-sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          creator_id: selectedCreator.oauth_connection_id,
          force_resync: forceResync
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        setError(`Erreur sync: ${result.error || 'Erreur inconnue'}`);
        console.error('Sync error:', result);
        return;
      }

      if (result.messages_synced > 0) {
        await loadEmails(selectedCreator.id);
        await analyzeThreads();
        await loadChatMessages(selectedCreator.id);
      } else {
        setError('Aucun nouvel email à synchroniser');
      }
    } catch (error) {
      console.error('Sync error:', error);
      setError(`Erreur: ${error.message || 'Erreur de synchronisation'}`);
    } finally {
      setSyncing(false);
    }
  }

  async function analyzeThreads() {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session || !selectedCreator) return;

    try {
      const response = await fetch(`${supabaseUrl}/functions/v1/ai-classify-summarize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          manager_id: session.user.id,
          creator_id: selectedCreator.id
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('AI analysis error:', error);
      }
    } catch (error) {
      console.error('Error calling AI analysis:', error);
    }
  }

  async function handleAction(actionType: string, emailId: string, threadId?: string, messageId?: string) {
    if (actionType === 'open_thread') {
      if (threadId) {
        const { data: threadEmails } = await supabase
          .from('email_messages')
          .select('*')
          .eq('gmail_thread_id', threadId)
          .eq('creator_id', selectedCreator?.id)
          .order('received_at', { ascending: true });

        if (threadEmails && threadEmails.length > 0) {
          setSelectedEmail(threadEmails[0]);
          setThreadMessages(threadEmails);
          return;
        }
      }
      await openEmailThread(emailId);
      return;
    }

    if (actionType === 'ignore') {
      if (messageId) {
        try {
          await supabase
            .from('ai_chat_messages')
            .delete()
            .eq('id', messageId);

          if (selectedCreator) {
            await loadChatMessages(selectedCreator.id);
          }
        } catch (error) {
          console.error('Error deleting message:', error);
        }
      }
      return;
    }

    try {
      console.log('Searching for email with ID:', emailId, 'for creator:', selectedCreator?.id);

      let email = null;

      const { data: emailById } = await supabase
        .from('email_messages')
        .select('*')
        .eq('id', emailId)
        .eq('creator_id', selectedCreator?.id)
        .maybeSingle();

      if (emailById) {
        email = emailById;
      } else {
        const { data: emailByGmailId } = await supabase
          .from('email_messages')
          .select('*')
          .eq('gmail_message_id', emailId)
          .eq('creator_id', selectedCreator?.id)
          .maybeSingle();

        email = emailByGmailId;
      }

      if (!email && threadId) {
        console.log('Email not found by ID, trying by thread_id:', threadId);
        const { data: threadEmails } = await supabase
          .from('email_messages')
          .select('*')
          .eq('gmail_thread_id', threadId)
          .eq('creator_id', selectedCreator?.id)
          .order('received_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        email = threadEmails;
      }

      if (!email) {
        console.log('Email not found with ID:', emailId);
        setError('Cet email n\'est plus disponible (peut-être supprimé lors d\'une synchronisation)');
        return;
      }

      console.log('Email found:', email.id);

      setSelectedEmail(email);

      setDraftContent('');
      setDraftSubject('Génération en cours...');
      setShowDraftModal(true);
      setStreamingDraft(true);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const requestPayload = {
        email_id: email.id,
        action_type: actionType,
        creator_name: selectedCreator?.name,
        creator_id: selectedCreator?.id,
        manager_name: user?.user_metadata?.full_name || 'Talent Manager',
      };

      console.log('[TM] Sending to ai-generate-draft:', requestPayload);

      const response = await fetch(`${supabaseUrl}/functions/v1/ai-generate-draft`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('AI generate draft error:', errorText);
        setStreamingDraft(false);
        setDraftContent('Erreur lors de la génération du brouillon. Veuillez réessayer.');
        setDraftSubject('Erreur');
        return;
      }

      setDraftSubject(`Re: ${email.subject}`);

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          accumulated += chunk;
          setDraftContent(accumulated);
        }
      }

      setStreamingDraft(false);
    } catch (error) {
      console.error('Draft error:', error);
      setStreamingDraft(false);
      setDraftContent('Erreur lors de la génération du brouillon. Veuillez réessayer.');
      setDraftSubject('Erreur');
    }
  }

  async function typewriterEffect(text: string) {
    const words = text.split(' ');
    let currentText = '';

    for (let i = 0; i < words.length; i++) {
      currentText += (i > 0 ? ' ' : '') + words[i];
      setDraftContent(currentText);
      await new Promise(resolve => setTimeout(resolve, 30));
    }
  }

  async function regenerateDraft(stylePrompt: string) {
    if (!selectedEmail || !draftContent) return;

    const currentDraftBackup = draftContent;
    setStreamingDraft(true);
    setDraftContent('');

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${supabaseUrl}/functions/v1/ai-generate-draft`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email_id: selectedEmail.id,
          action_type: 'regenerate',
          creator_name: selectedCreator?.name,
          creator_id: selectedCreator?.id,
          manager_name: user?.user_metadata?.full_name || 'Talent Manager',
          style_instruction: stylePrompt,
          current_draft: currentDraftBackup,
        }),
      });

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          accumulated += chunk;
          setDraftContent(accumulated);
        }
      }

      setStreamingDraft(false);
    } catch (error) {
      console.error('Regenerate draft error:', error);
      setStreamingDraft(false);
      setError('Erreur lors de la régénération du draft');
    }
  }

  async function openEmailThread(emailId: string) {
    try {
      const { data: email } = await supabase
        .from('email_messages')
        .select('*')
        .eq('id', emailId)
        .maybeSingle();

      if (email) {
        setSelectedEmail(email);
        await loadThreadMessages(email.gmail_thread_id);
        setShowThreadModal(true);
      }
    } catch (error) {
      console.error('Error opening thread:', error);
      setError('Erreur lors de l\'ouverture du fil de discussion');
    }
  }

  async function sendChatMessage() {
    if (!chatInput.trim() || !selectedCreator) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setLoading(true);
    setError(null);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Session expirée');
        return;
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/ai-chat-router`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          creator_id: selectedCreator.id,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        setError(`Erreur IA: ${result.error || 'Erreur inconnue'}`);
        console.error('Chat error:', result);
        return;
      }

      await loadChatMessages(selectedCreator.id);
    } catch (error) {
      console.error('Chat error:', error);
      setError(`Erreur: ${error.message || 'Erreur de chat'}`);
    } finally {
      setLoading(false);
    }
  }

  async function sendEmail() {
    console.log('sendEmail called', {
      selectedEmail,
      draftContent,
      draftContentLength: draftContent?.length,
      draftSubject,
      attachmentsCount: attachments.length
    });

    if (!selectedEmail) {
      setError('Aucun email sélectionné');
      return;
    }

    if (!draftContent || draftContent.trim() === '') {
      setError('Le contenu du draft est vide');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Session non trouvée');
        return;
      }

      let uploadedAttachments: Array<{filename: string, url: string, size: number}> = [];

      if (attachments.length > 0) {
        for (const file of attachments) {
          const filePath = `email-attachments/${user?.id}/${Date.now()}-${file.name}`;
          const { data, error: uploadError } = await supabase.storage
            .from('resources')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) {
            throw new Error(`Erreur upload ${file.name}: ${uploadError.message}`);
          }

          const { data: { publicUrl } } = supabase.storage
            .from('resources')
            .getPublicUrl(filePath);

          uploadedAttachments.push({
            filename: file.name,
            url: publicUrl,
            size: file.size
          });
        }
      }

      const recipientEmail = getReplyRecipient(selectedEmail, selectedCreator?.email);

      const response = await fetch(`${supabaseUrl}/functions/v1/gmail-send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: recipientEmail,
          subject: draftSubject,
          body: draftContent,
          thread_id: selectedEmail.gmail_thread_id,
          cc_creator: ccCreator,
          creator_id: selectedCreator?.id,
          send_as_creator: sendAsCreator,
          cc_manager: sendAsCreator,
          reply_to_message_id: selectedEmail.id,
          attachments: uploadedAttachments,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors de l\'envoi');
      }

      let statusMessage = 'Mail envoyé avec succès';
      if (sendAsCreator && ccManager) {
        statusMessage += ' (créateur + TM en CC)';
      } else if (sendAsCreator) {
        statusMessage += ' (depuis le créateur)';
      } else if (ccCreator) {
        statusMessage += ' (TM + créateur en CC)';
      }

      setShowDraftModal(false);
      setDraftContent('');
      setAttachments([]);

      setNotification(statusMessage);
      setTimeout(() => setNotification(null), 3000);

      if (selectedCreator) {
        await loadChatMessages(selectedCreator.id);
      }
    } catch (error) {
      console.error('Send error:', error);
      setError(`Erreur d'envoi: ${error.message || 'Erreur inconnue'}`);
    } finally {
      setLoading(false);
    }
  }


  const getClassificationBadge = (classification?: string) => {
    const colors: Record<string, string> = {
      collab: 'bg-blue-100 text-blue-800',
      gift: 'bg-green-100 text-green-800',
      event: 'bg-purple-100 text-purple-800',
      admin: 'bg-gray-100 text-gray-800',
      spam: 'bg-red-100 text-red-800',
    };
    return colors[classification || 'other'] || 'bg-gray-100 text-gray-800';
  };

  const hasReplied = (email: any) => {
    return threadMessages.some(msg =>
      msg.gmail_thread_id === email.gmail_thread_id &&
      msg.labels?.includes('SENT')
    );
  };

  const getClassificationLabel = (classification?: string) => {
    const labels: Record<string, string> = {
      collab: 'Collaboration',
      gift: 'Cadeau',
      event: 'Événement',
      admin: 'Administratif',
      spam: 'Spam',
      other: 'Autre'
    };
    return labels[classification || 'other'] || 'Autre';
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden relative">
      {notification && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in">
          <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
            <Check className="w-5 h-5" />
            <span className="font-medium">{notification}</span>
          </div>
        </div>
      )}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Talent Manager</h2>
          <p className="text-sm text-gray-600">
            Gérez les emails de vos créateurs
          </p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {creators.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <p className="mb-2">Aucun créateur trouvé</p>
              <p className="text-sm">Ajoutez des influenceurs depuis la page Créateurs</p>
            </div>
          ) : (
            creators.map((creator) => (
              <div
                key={creator.id}
                className={`p-4 border-b border-gray-100 ${
                  selectedCreator?.id === creator.id ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setSelectedCreator(creator)}
                    className="flex items-center gap-3 flex-1 text-left"
                  >
                    <InfluencerAvatar
                      profileImageUrl={creator.avatar}
                      name={creator.name}
                      size="md"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{creator.name}</div>
                      <div className="text-xs text-gray-500">
                        {creator.gmail_connected ? (
                          <span className="text-green-600">
                            {creator.connected_email || 'Gmail connecté'}
                          </span>
                        ) : (
                          <span className="text-gray-400">{creator.email || 'Non connecté'}</span>
                        )}
                      </div>
                    </div>
                  </button>
                  {creator.gmail_connected ? (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (confirm('Êtes-vous sûr de vouloir déconnecter Gmail pour ce créateur ?')) {
                          try {
                            await supabase
                              .from('oauth_connections')
                              .update({ is_active: false })
                              .eq('manager_id', user.id)
                              .eq('creator_id', creator.id);

                            await loadCreators();
                            setNotification('Gmail déconnecté avec succès');
                            setTimeout(() => setNotification(null), 3000);
                          } catch (error) {
                            console.error('Erreur de déconnexion:', error);
                            setError('Erreur lors de la déconnexion');
                          }
                        }
                      }}
                      className="group relative flex items-center justify-center w-8 h-8 bg-white border border-gray-200 rounded-full hover:border-red-300 hover:bg-red-50 transition-all duration-200 shadow-sm hover:shadow"
                      title="Déconnecter Gmail"
                    >
                      <X className="w-4 h-4 text-gray-400 group-hover:text-red-600 transition-colors duration-200" />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        connectGmail(creator.id);
                      }}
                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      Connecter Gmail
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        {selectedCreator ? (
          <>
            <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedCreator.gmail_connected ? `📬 ${selectedCreator.name}` : `Chat IA - ${selectedCreator.name}`}
              </h3>
              <div className="flex gap-2">
                {!selectedCreator.gmail_connected ? (
                  <button
                    onClick={() => connectGmail(selectedCreator.id)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Connecter Gmail
                  </button>
                ) : (
                  <button
                    onClick={async () => {
                      if (!selectedCreator?.oauth_connection_id) return;
                      setSyncing(true);
                      try {
                        await syncCreatorEmails(selectedCreator.oauth_connection_id);
                        await loadEmails(selectedCreator.id);
                      } catch (err) {
                        console.error('Sync error:', err);
                      }
                      setSyncing(false);
                    }}
                    disabled={syncing}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                    Synchroniser
                  </button>
                )}
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border-b border-red-200 p-4 flex items-center justify-between">
                <p className="text-red-800 text-sm">{error}</p>
                <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {selectedCreator.gmail_connected ? (
              <div className="flex-1 flex overflow-hidden">
                <div className="w-96 border-r border-gray-200">
                  <EmailThreadList
                    threads={prepareThreadsForList()}
                    selectedThreadId={selectedEmail?.gmail_thread_id || null}
                    onThreadSelect={handleThreadSelect}
                    loading={loading}
                  />
                </div>

                <div className="flex-1">
                  {viewMode === 'thread' && threadMessages.length > 0 ? (
                    <EmailThreadView
                      messages={threadMessages}
                      subject={threadMessages[0]?.subject || ''}
                      onBack={handleBackToList}
                      onReply={() => setShowReplyComposer(true)}
                      loading={loading}
                    />
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-500">
                      <div className="text-center">
                        <Mail className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                        <p>Sélectionnez un thread pour commencer</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500 bg-gray-50">
                <div className="text-center">
                  <Mail className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-sm">Connectez Gmail pour voir les emails</p>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <Mail className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>Sélectionnez un créateur pour commencer</p>
            </div>
          </div>
        )}
      </div>

      {showDraftModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Modifier le draft</h3>
                <button onClick={() => setShowDraftModal(false)}>
                  <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Objet
                  </label>
                  <input
                    type="text"
                    value={draftSubject}
                    onChange={(e) => setDraftSubject(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Message
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => regenerateDraft('plus court et direct')}
                        disabled={streamingDraft}
                        className="px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded hover:bg-purple-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ✂️ Plus court
                      </button>
                      <button
                        onClick={() => regenerateDraft('plus professionnel et formel')}
                        disabled={streamingDraft}
                        className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        👔 Plus pro
                      </button>
                      <button
                        onClick={() => regenerateDraft('plus chaleureux et amical')}
                        disabled={streamingDraft}
                        className="px-2 py-1 text-xs bg-orange-50 text-orange-700 rounded hover:bg-orange-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        😊 Plus amical
                      </button>
                      <button
                        onClick={() => regenerateDraft('plus enthousiaste et motivé')}
                        disabled={streamingDraft}
                        className="px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        🚀 Plus enthousiaste
                      </button>
                    </div>
                  </div>
                  <div className="relative">
                    <textarea
                      value={draftContent}
                      onChange={(e) => setDraftContent(e.target.value)}
                      rows={12}
                      disabled={streamingDraft}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm disabled:bg-gray-50 disabled:cursor-wait"
                    />
                    {streamingDraft && (
                      <div className="absolute top-3 right-3">
                        <div className="flex items-center space-x-2 bg-blue-100 px-3 py-1.5 rounded-full shadow-sm">
                          <div className="flex space-x-1">
                            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                          <span className="text-xs text-blue-700 font-medium">Génération en cours...</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pièces jointes
                  </label>
                  <div className="space-y-2">
                    <input
                      type="file"
                      multiple
                      onChange={(e) => {
                        if (e.target.files) {
                          setAttachments(prev => [...prev, ...Array.from(e.target.files!)]);
                        }
                      }}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter des fichiers
                    </label>
                    {attachments.length > 0 && (
                      <div className="space-y-2 mt-3">
                        {attachments.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-medium text-blue-700">{file.name.split('.').pop()?.toUpperCase()}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                                <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                              </div>
                            </div>
                            <button
                              onClick={() => setAttachments(prev => prev.filter((_, i) => i !== index))}
                              className="ml-2 p-1 text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Options d'envoi</div>
                  <div className="flex flex-col gap-3">
                    <label className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={ccCreator}
                        onChange={(e) => setCcCreator(e.target.checked)}
                        className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 transition-all"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900 group-hover:text-blue-700 transition-colors">CC Créateur</span>
                        <p className="text-xs text-gray-500 mt-0.5">Le créateur recevra une copie de l'email</p>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={sendAsCreator}
                        onChange={(e) => setSendAsCreator(e.target.checked)}
                        className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 transition-all"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900 group-hover:text-blue-700 transition-colors">Envoyer en tant que créateur</span>
                        <p className="text-xs text-gray-500 mt-0.5">L'email sera envoyé depuis le compte Gmail du créateur</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={sendEmail}
                    disabled={loading || streamingDraft}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4" />
                    Envoyer
                  </button>
                  <button
                    onClick={() => setShowDraftModal(false)}
                    disabled={streamingDraft}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showThreadModal && selectedEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex justify-between items-start">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{selectedEmail.subject}</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Thread ID: {selectedEmail.gmail_thread_id}
                </p>
              </div>
              <button
                onClick={() => setShowThreadModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {threadMessages.length === 0 ? (
                <p className="text-gray-500 text-center">Aucun message dans ce fil</p>
              ) : (
                threadMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-4 rounded-lg ${
                      msg.direction === 'out'
                        ? 'bg-blue-50 border border-blue-200'
                        : 'bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium text-gray-900">
                          {msg.direction === 'out' ? 'Envoyé' : 'Reçu'}
                        </p>
                        <p className="text-sm text-gray-600">{msg.from_addr}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-500">
                          {new Date(msg.received_at).toLocaleString('fr-FR')}
                        </p>
                        {msg.direction === 'in' && (
                          <button
                            onClick={() => {
                              setSelectedEmail(msg);
                              setShowThreadModal(false);
                              handleAction('reply', msg.id);
                            }}
                            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-1"
                          >
                            <Send className="w-3 h-3" />
                            Répondre
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-gray-700 whitespace-pre-wrap">
                      {cleanEmailContent(decodeHtmlEntities(msg.body_text))}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowThreadModal(false);
                  handleAction('reply', threadMessages[threadMessages.length - 1]?.id || selectedEmail.id);
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" />
                Répondre au dernier message
              </button>
            </div>
          </div>
        </div>
      )}

      {showReplyComposer && selectedEmail && selectedCreator && threadMessages.length > 0 && (
        <EmailReplyComposer
          threadMessages={threadMessages}
          creatorEmail={selectedCreator.connected_email || selectedCreator.email || ''}
          creatorName={selectedCreator.name}
          creatorId={selectedCreator.id}
          managerEmail={user?.email}
          isManagerView={false}
          collaborations={collaborations}
          onClose={() => setShowReplyComposer(false)}
          onSend={handleSendReply}
        />
      )}
    </div>
  );
}
